/* eslint-disable react/no-array-index-key */
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Button, Checkbox, Paragraph, Separator } from '@fs/zion-ui'
import { useEventBuffer } from '../../hooks/useEventCapture'
import useNotifications from '../../hooks/useNotifications'

// ---------------------------------------------------------------------------
// Selection state helpers
// ---------------------------------------------------------------------------

const NETWORK_CHILD_KEYS = [
  'networkUrl',
  'networkQueryParams',
  'networkHeaders',
  'networkRequestBody',
  'networkResponseBody',
  'networkStatusCode',
]

function buildInitialSelections() {
  return {
    screenshot: true,
    network: true,
    networkUrl: true,
    networkQueryParams: true,
    networkHeaders: true,
    networkRequestBody: true,
    networkResponseBody: true,
    networkStatusCode: true,
    networkIndeterminate: false,
    navigation: true,
    console: true,
    errors: true,
  }
}

function selectionsReducer(state, action) {
  if (action.type === 'set') {
    return { ...state, [action.key]: action.value }
  }
  if (action.type === 'toggle') {
    const { key } = action
    const next = { ...state, [key]: !state[key] }

    if (key === 'network') {
      NETWORK_CHILD_KEYS.forEach((child) => {
        next[child] = next.network
      })
      next.networkIndeterminate = false
    }

    if (NETWORK_CHILD_KEYS.includes(key)) {
      const allChecked = NETWORK_CHILD_KEYS.every((k) => next[k])
      const someChecked = NETWORK_CHILD_KEYS.some((k) => next[k])
      next.network = allChecked || someChecked
      next.networkIndeterminate = !allChecked && someChecked
    }

    return next
  }
  return state
}

// ---------------------------------------------------------------------------
// Screenshot capture
// ---------------------------------------------------------------------------

async function captureScreenshot(containerRef) {
  if (containerRef.current) {
    containerRef.current.style.visibility = 'hidden'
  }

  try {
    const stream = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      audio: false,
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
    })

    return await new Promise((resolve, reject) => {
      const video = document.createElement('video')
      video.srcObject = stream
      video.onloadedmetadata = () => {
        video.play()
        const canvas = document.createElement('canvas')
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        canvas.getContext('2d').drawImage(video, 0, 0)
        stream.getTracks().forEach((t) => t.stop())
        video.srcObject = null
        try {
          resolve(canvas.toDataURL('image/png'))
        } catch (err) {
          reject(err)
        }
      }
      video.onerror = reject
    })
  } finally {
    if (containerRef.current) {
      containerRef.current.style.visibility = ''
    }
  }
}

// ---------------------------------------------------------------------------
// Filter/assemble helpers
// ---------------------------------------------------------------------------

function filterEvents(events, selections) {
  return events.filter((evt) => {
    if (evt.type === 'network') return selections.network
    if (evt.type === 'navigation') return selections.navigation
    if (evt.type === 'console') return selections.console
    if (evt.type === 'error') return selections.errors
    return true
  })
}

function applyNetworkFieldSelections(event, selections) {
  if (event.type !== 'network') return event
  const result = { ...event }
  if (!selections.networkUrl) delete result.url
  if (!selections.networkQueryParams) delete result.queryParams
  if (!selections.networkHeaders) {
    delete result.requestHeaders
    delete result.responseHeaders
  }
  if (!selections.networkRequestBody) delete result.requestBody
  if (!selections.networkResponseBody) delete result.responseBody
  if (!selections.networkStatusCode) {
    delete result.status
    delete result.statusText
  }
  return result
}

function assembleReport({ events, selections, screenshotDataUrl }) {
  const filtered = filterEvents(events, selections).map((evt) => applyNetworkFieldSelections(evt, selections))
  return {
    timestamp: new Date().toISOString(),
    pageUrl: window.location.href,
    userAgent: navigator.userAgent,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    events: filtered,
    screenshotDataUrl: screenshotDataUrl || undefined,
  }
}

// ---------------------------------------------------------------------------
// Indeterminate checkbox — sets DOM property imperatively
// ---------------------------------------------------------------------------

function IndeterminateCheckbox({ indeterminate, ...props }) {
  const ref = useRef(null)

  useEffect(() => {
    if (ref.current) {
      // zion-ui Checkbox renders an <input> inside; walk to it
      const input = ref.current.querySelector('input[type="checkbox"]') || ref.current
      input.indeterminate = Boolean(indeterminate)
    }
  }, [indeterminate])

  return (
    <span ref={ref}>
      <Checkbox {...props} />
    </span>
  )
}

// ---------------------------------------------------------------------------
// NetworkChildrenPanel
// ---------------------------------------------------------------------------

const NETWORK_LABEL = {
  networkUrl: 'URL',
  networkQueryParams: 'Query Parameters',
  networkHeaders: 'Headers',
  networkRequestBody: 'Request Body',
  networkResponseBody: 'Response Body',
  networkStatusCode: 'Status Code',
}

function NetworkChildrenPanel({ selections, dispatch }) {
  return (
    <div style={{ paddingInlineStart: 24, marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {NETWORK_CHILD_KEYS.map((key) => (
        <Checkbox
          key={key}
          name={key}
          label={NETWORK_LABEL[key]}
          checked={selections[key]}
          onChange={() => dispatch({ type: 'toggle', key })}
        />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// DataSelectionPanel
// ---------------------------------------------------------------------------

function DataSelectionPanel({
  selections,
  dispatch,
  showNetworkChildren,
  setShowNetworkChildren,
  screenshot,
  onCaptureScreenshot,
  screenshotError,
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <Checkbox
          name="screenshot"
          label="Screenshot"
          checked={selections.screenshot}
          onChange={() => dispatch({ type: 'toggle', key: 'screenshot' })}
        />
        {!screenshot ? (
          <Button size="sm" emphasis="medium" onClick={onCaptureScreenshot}>
            Capture now
          </Button>
        ) : (
          <span style={{ color: 'green', fontSize: 13 }}>Captured ✓</span>
        )}
      </div>
      {screenshotError && <p style={{ color: 'red', fontSize: 12, margin: '-4px 0 0' }}>{screenshotError}</p>}

      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <IndeterminateCheckbox
            name="network"
            label="Requests & Responses"
            checked={!selections.networkIndeterminate && selections.network}
            indeterminate={selections.networkIndeterminate}
            onChange={() => dispatch({ type: 'toggle', key: 'network' })}
          />
          <Button size="sm" emphasis="low" onClick={() => setShowNetworkChildren((v) => !v)}>
            {showNetworkChildren ? 'Hide options' : 'Show options'}
          </Button>
        </div>
        {showNetworkChildren && <NetworkChildrenPanel selections={selections} dispatch={dispatch} />}
      </div>

      <Checkbox
        name="navigation"
        label="Navigation"
        checked={selections.navigation}
        onChange={() => dispatch({ type: 'toggle', key: 'navigation' })}
      />
      <Checkbox
        name="console"
        label="Console"
        checked={selections.console}
        onChange={() => dispatch({ type: 'toggle', key: 'console' })}
      />
      <Checkbox
        name="errors"
        label="Errors"
        checked={selections.errors}
        onChange={() => dispatch({ type: 'toggle', key: 'errors' })}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// EventLogPreview
// ---------------------------------------------------------------------------

function EventLogPreview({ events, selections }) {
  const filtered = filterEvents(events, selections)
  const recent = [...filtered].reverse()

  return (
    <div style={{ maxHeight: 200, overflowY: 'auto', fontFamily: 'monospace', fontSize: 11 }}>
      {recent.length === 0 && <Paragraph size="sm">No events captured.</Paragraph>}
      {recent.map((evt, i) => (
        <div key={i} data-testid="event-log-row" style={{ borderBottom: '1px solid #eee', padding: '2px 0' }}>
          <strong>{evt.type}</strong>
          {evt.subtype && ` (${evt.subtype})`}
          {evt.method && ` ${evt.method}`}
          {evt.url && ` ${evt.url}`}
          {evt.status && ` ${evt.status}`}
          {' — '}
          {evt.timestamp}
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// PayloadPreview
// ---------------------------------------------------------------------------

function PayloadPreview({ events, selections }) {
  const report = useMemo(
    () =>
      assembleReport({
        events,
        selections,
        screenshotDataUrl: selections.screenshot ? '[screenshot data]' : undefined,
      }),
    [events, selections]
  )

  return (
    <pre
      data-testid="payload-preview"
      style={{
        maxHeight: 200,
        overflowY: 'auto',
        fontSize: 11,
        background: '#f5f5f5',
        padding: 8,
        margin: 0,
      }}
    >
      {JSON.stringify(report, null, 2)}
    </pre>
  )
}

// ---------------------------------------------------------------------------
// ReportModal
// ---------------------------------------------------------------------------

export default function ReportModal({ onClose, onSubmit, collector }) {
  const allEvents = useEventBuffer(collector)
  const { ok, error: notifyError } = useNotifications()
  const [selections, dispatch] = useReducer(selectionsReducer, buildInitialSelections())
  const [showNetworkChildren, setShowNetworkChildren] = useState(false)
  const [screenshot, setScreenshot] = useState(null)
  const [screenshotError, setScreenshotError] = useState(null)
  const [submitState, setSubmitState] = useState('idle')
  const containerRef = useRef(null)

  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') onClose?.()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  const handleOutsideClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) onClose?.()
    },
    [onClose]
  )

  const handleScreenshot = useCallback(async () => {
    setScreenshotError(null)
    try {
      const dataUrl = await captureScreenshot(containerRef)
      setScreenshot(dataUrl)
      dispatch({ type: 'set', key: 'screenshot', value: true })
    } catch (err) {
      if (err?.name !== 'NotAllowedError') {
        setScreenshotError('Screenshot failed. Please try again.')
      }
    }
  }, [])

  const handleSubmit = useCallback(async () => {
    setSubmitState('loading')

    let capturedScreenshot = screenshot
    if (selections.screenshot && !capturedScreenshot) {
      try {
        capturedScreenshot = await captureScreenshot(containerRef)
        setScreenshot(capturedScreenshot)
      } catch {
        // Capture failed or was cancelled — submit without screenshot
        capturedScreenshot = null
      }
    }

    try {
      const report = assembleReport({
        events: allEvents,
        selections,
        screenshotDataUrl: selections.screenshot ? capturedScreenshot : null,
      })
      await onSubmit?.(report)
      setSubmitState('success')
      ok('Report submitted successfully.')
      onClose?.()
    } catch (err) {
      setSubmitState('idle')
      notifyError(err?.message || 'Report submission failed.', err)
    }
  }, [allEvents, notifyError, ok, onClose, onSubmit, screenshot, selections])

  const isSubmitting = submitState === 'loading'
  const isSuccess = submitState === 'success'

  const modalContent = (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      data-testid="report-modal-backdrop"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      onClick={handleOutsideClick}
    >
      <dialog
        ref={containerRef}
        open
        aria-label="Submit a Report"
        style={{
          background: '#fff',
          borderRadius: 8,
          width: 600,
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflowY: 'auto',
          padding: 24,
          border: 'none',
          boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: 20,
            paddingBottom: 16,
            borderBottom: '1px solid #e5e5e5',
          }}
        >
          <h2 style={{ margin: 0, flexGrow: 1, fontSize: 22, fontWeight: 700, letterSpacing: '-0.3px' }}>
            Submit a Report
          </h2>
          <Button emphasis="low" onClick={onClose} aria-label="Close">
            ✕
          </Button>
        </div>

        <DataSelectionPanel
          selections={selections}
          dispatch={dispatch}
          showNetworkChildren={showNetworkChildren}
          setShowNetworkChildren={setShowNetworkChildren}
          screenshot={screenshot}
          onCaptureScreenshot={handleScreenshot}
          screenshotError={screenshotError}
        />

        <Separator size="sm" />

        <details style={{ marginBottom: 8 }}>
          <summary style={{ cursor: 'pointer', fontWeight: 600, padding: '4px 0' }}>
            {`Event log (${filterEvents(allEvents, selections).length} events)`}
          </summary>
          <div style={{ marginTop: 8 }}>
            <EventLogPreview events={allEvents} selections={selections} />
          </div>
        </details>

        <details>
          <summary style={{ cursor: 'pointer', fontWeight: 600, padding: '4px 0' }}>JSON payload preview</summary>
          <div style={{ marginTop: 8 }}>
            <PayloadPreview events={allEvents} selections={selections} />
          </div>
        </details>

        <Separator size="sm" />

        <div style={{ display: 'flex', gap: 12 }}>
          <Button emphasis="medium" onClick={onClose}>
            Cancel
          </Button>
          <Button emphasis="high" onClick={handleSubmit} disabled={isSubmitting || isSuccess}>
            {isSubmitting ? 'Submitting…' : 'Submit'}
          </Button>
        </div>
      </dialog>
    </div>
  )

  return createPortal(modalContent, document.body)
}
